import { SupportedChainId } from 'config/chains'
import { INFRARED_CHARM_VAULTS } from 'constants/lists'
import React, { useEffect, useState } from 'react'
import { AlertTriangle, Info, X } from 'react-feather'
import styled, { keyframes } from 'styled-components'
import { ThemedText } from 'theme'
import { ExternalLink } from 'theme/components'

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`

const FixedBannerWrapper = styled.div`
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  z-index: 2;
  animation: ${slideDown} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`

const BannerWrapper = styled.div`
  background: linear-gradient(90deg, #121626, #151b30);
  padding: 16px 0;
  display: flex;
  align-items: center;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;

    background: linear-gradient(
      90deg,
      rgba(59, 130, 246, 0) 0%,
      rgba(59, 130, 246, 0.8) 50%,
      rgba(59, 130, 246, 0) 100%
    );
  }
`

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  gap: 12px;
`

const WarningGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const WarningIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.warning500};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  color: ${({ theme }) => theme.textTertiary};
  flex-shrink: 0;
`

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.accentAction};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  color: white;
  flex-shrink: 0;
`

const TextContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.textPrimary};
  text-align: left;
  line-height: 1.4;

  @media (max-width: 600px) {
    font-size: 13px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  flex-shrink: 0;
  position: absolute;
  right: 24px;
  top: 16px;

  &:hover {
    color: #e2e8f0;
    background-color: rgba(255, 255, 255, 0.05);
  }
`
const CenteredCloseButton = styled(CloseButton)`
  top: 50%;
  transform: translateY(-40%);
`

export function CharmWarningBanner({ poolAddress, chainId }: { poolAddress: string; chainId: SupportedChainId }) {
  const [isVisible, setIsVisible] = useState(true)
  const isInfraredManagedVault = INFRARED_CHARM_VAULTS[chainId]?.includes(poolAddress.toLowerCase())

  useEffect(() => {
    if (isVisible) {
      document.body.style.paddingTop = '68px'
    } else {
      document.body.style.paddingTop = '0'
    }

    return () => {
      document.body.style.paddingTop = '0'
    }
  }, [isVisible])

  if (!isVisible) {
    return null
  }

  return (
    <FixedBannerWrapper>
      <BannerWrapper>
        <BannerContent>
          <WarningGroup style={{ justifyContent: 'flex-start' }}>
            <WarningIconWrapper>
              <AlertTriangle size={16} />
            </WarningIconWrapper>
            <ThemedText.DeprecatedBody fontSize={14}>
              The pool&apos;s smart contracts were created using Charm Finance.{' '}
              <ExternalLink
                href="https://x.com/CharmFinance/status/1940418269233741832"
                style={{ color: '#60a5fa', textDecoration: 'none' }}
              >
                Learn more ↗
              </ExternalLink>
            </ThemedText.DeprecatedBody>
          </WarningGroup>

          <WarningGroup>
            <InfoIconWrapper>
              <Info size={16} />
            </InfoIconWrapper>
            <ThemedText.DeprecatedBody fontSize={14}>
              {isInfraredManagedVault
                ? 'This pool is managed by Infrared'
                : 'This pool uses a strategy that is more concentrated than the typical Kodiak Island'}
              , please check details on{' '}
              <ExternalLink
                href={` https://app.charm.fi/berachain/vault/${poolAddress}`}
                style={{ color: '#60a5fa', textDecoration: 'none' }}
              >
                Charm's website ↗
              </ExternalLink>
            </ThemedText.DeprecatedBody>
          </WarningGroup>

          <CloseButton onClick={() => setIsVisible(false)}>
            <X size={16} />
          </CloseButton>
        </BannerContent>
      </BannerWrapper>
    </FixedBannerWrapper>
  )
}

export function YbgtWarningBanner() {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (isVisible) {
      document.body.style.paddingTop = '28px'
    } else {
      document.body.style.paddingTop = '0'
    }

    return () => {
      document.body.style.paddingTop = '0'
    }
  }, [isVisible])

  if (!isVisible) {
    return null
  }

  return (
    <FixedBannerWrapper>
      <BannerWrapper>
        <BannerContent>
          <WarningGroup style={{ justifyContent: 'flex-start' }}>
            <WarningIconWrapper>
              <AlertTriangle size={16} />
            </WarningIconWrapper>
            <ThemedText.DeprecatedBody fontSize={14}>
              yBGT liquidity is being transitioned as part of the overall transition to Ultraviolet.{' '}
              <ExternalLink
                href="https://x.com/UV_Money/status/1950277725492822398"
                style={{ color: '#60a5fa', textDecoration: 'none' }}
              >
                Learn more ↗
              </ExternalLink>
            </ThemedText.DeprecatedBody>
          </WarningGroup>
          <CenteredCloseButton onClick={() => setIsVisible(false)}>
            <X size={16} />
          </CenteredCloseButton>
        </BannerContent>
      </BannerWrapper>
    </FixedBannerWrapper>
  )
}
